import type {
  HoldingUnitCapabilities,
  HoldingUnitContainerKind,
  HoldingUnitIdentityMode,
  HoldingUnitProductKind,
  HoldingUnitProfile,
  HoldingUnitType,
} from '../holding-units/holding-units.dto.js';
import type { TareProfileMode, TareProfileSummaryDto } from '../tare-profiles/tare-profiles.dto.js';

export const unitProfileCategoryValues = ['source', 'staging', 'product', 'leftover'] as const;
export type UnitProfileCategory = (typeof unitProfileCategoryValues)[number];

export const unitProfileFixedIdPolicyValues = ['none', 'fixed_asset_required'] as const;
export type UnitProfileFixedIdPolicy = (typeof unitProfileFixedIdPolicyValues)[number];

export const unitProfileTemplateKeyValues = [
  'fixed_id_source_heavy_container',
  'source_loose',
  'source_bag',
  'source_heavy_container',
  'staging_container',
  'product_none',
  'product_bale',
  'product_paca',
  'leftover_loose',
  'leftover_bag',
  'leftover_heavy_container',
] as const;
export type UnitProfileTemplateKey = (typeof unitProfileTemplateKeyValues)[number];

export const unitProfileWorkflowValues = [
  'fast_weigh_create',
  'fixed_id_asset_start',
  'build_product_input',
  'build_product_output',
  'build_product_leftover',
  'inbound_receipt_product',
  'inbound_receipt_unit',
  'transfer_source',
  'shipment_candidate',
] as const;
export type UnitProfileWorkflow = (typeof unitProfileWorkflowValues)[number];

export const unitProfileValidationIssueCodeValues = [
  'invalid_template_key',
  'invalid_tuple_family',
  'template_tuple_mismatch',
  'fixed_id_policy_mismatch',
  'profile_category_mismatch',
  'capability_exceeds_template',
  'tare_profile_incompatible',
  'structural_edit_not_allowed',
] as const;
export type UnitProfileValidationIssueCode = (typeof unitProfileValidationIssueCodeValues)[number];

export type UnitProfileCapabilityPolicy = HoldingUnitCapabilities;
export type UnitProfileCapabilityName = keyof HoldingUnitCapabilities;

export interface UnitProfileTemplateDto extends HoldingUnitProfile {
  templateKey: UnitProfileTemplateKey;
  profileCategory: UnitProfileCategory;
  fixedIdPolicy: UnitProfileFixedIdPolicy;
  displayName: string;
  description?: string | null;
  maxCapabilities: UnitProfileCapabilityPolicy;
  defaultCapabilityPolicy: UnitProfileCapabilityPolicy;
  allowedTareModes: TareProfileMode[];
}

export interface UnitProfileDto extends HoldingUnitProfile {
  id: string; // UUID
  companyId: string;
  storeId: string;
  locationId: string;
  code: string;
  displayName: string;
  description?: string | null;
  templateKey: UnitProfileTemplateKey;
  profileCategory: UnitProfileCategory;
  fixedIdPolicy: UnitProfileFixedIdPolicy;
  tareProfileId?: string | null;
  capabilityPolicy: UnitProfileCapabilityPolicy;
  isActive: boolean;
  displayOrder: number;
  revision: number;
  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
}

export interface UnitProfileValidationIssue {
  code: UnitProfileValidationIssueCode;
  field?: string;
  message: string;
}

export interface UnitProfileValidationResult {
  isValid: boolean;
  issues: UnitProfileValidationIssue[];
}

export interface ListUnitProfileTemplatesResponse {
  items: UnitProfileTemplateDto[];
}

export interface ListUnitProfilesByLocationRequest {
  companyId: string;
  storeId: string;
  locationId: string;
  includeInactive?: boolean;
  profileCategory?: UnitProfileCategory;
  templateKey?: UnitProfileTemplateKey;
}

export interface ListUnitProfilesByLocationResponse {
  items: UnitProfileDto[];
}

export interface CreateUnitProfileRequest {
  companyId: string;
  storeId: string;
  locationId: string;
  code: string;
  displayName: string;
  description?: string | null;
  templateKey: UnitProfileTemplateKey;
  tareProfileId?: string | null;
  capabilityPolicy?: Partial<UnitProfileCapabilityPolicy>;
  isActive?: boolean;
  displayOrder?: number;
}

export interface CreateUnitProfileResponse {
  item: UnitProfileDto;
}

export interface UpdateUnitProfileRequest {
  companyId: string;
  unitProfileId: string;
  displayName?: string;
  description?: string | null;
  tareProfileId?: string | null;
  capabilityPolicy?: Partial<UnitProfileCapabilityPolicy>;
  isActive?: boolean;
  displayOrder?: number;
}

export interface UpdateUnitProfileResponse {
  item: UnitProfileDto;
}

export interface GetUnitProfileResponse {
  item: UnitProfileDto;
}

export interface CloneUnitProfileRequest {
  companyId: string;
  sourceUnitProfileId: string;
  code: string;
  displayName: string;
  description?: string | null;
  tareProfileId?: string | null;
  capabilityPolicy?: Partial<UnitProfileCapabilityPolicy>;
  isActive?: boolean;
  displayOrder?: number;
}

export interface CloneUnitProfileResponse {
  item: UnitProfileDto;
}

export interface UnitProfileWorkflowOptionDto extends HoldingUnitProfile {
  id: string;
  code: string;
  displayName: string;
  description?: string | null;
  templateKey: UnitProfileTemplateKey;
  profileCategory: UnitProfileCategory;
  fixedIdPolicy: UnitProfileFixedIdPolicy;
  tareProfileId?: string | null;
  tareProfile?: TareProfileSummaryDto | null;
  capabilityPolicy: UnitProfileCapabilityPolicy;
  displayOrder: number;
  revision: number;
}

export interface ListUnitProfileWorkflowOptionsRequest {
  companyId: string;
  storeId: string;
  locationId: string;
  workflow: UnitProfileWorkflow;
  includeInactive?: boolean;
}

export interface ListUnitProfileWorkflowOptionsResponse {
  items: UnitProfileWorkflowOptionDto[];
}

export interface ResolvedUnitProfileSnapshotFields {
  unitProfileId: string;
  unitProfileRevision: number;
  unitProfileDisplayName: string;
  unitProfileTemplateKey: UnitProfileTemplateKey;
  profileCategory: UnitProfileCategory;
  unitType: HoldingUnitType;
  identityMode: HoldingUnitIdentityMode;
  containerKind: HoldingUnitContainerKind;
  productKind: HoldingUnitProductKind;
  fixedIdPolicy: UnitProfileFixedIdPolicy;
  capabilityPolicy: UnitProfileCapabilityPolicy;
}
